/**
 * IngestContext.tsx
 *
 * PURPOSE:
 * Shares ingestion form state across the AddTable page without prop drilling.
 * Holds the DataProfile from file validation and form submission errors.
 *
 * WHAT THIS MANAGES:
 * ✓ DataProfile from file validation (columns, statistics)
 * ✓ Form submission errors
 *
 * FUTURE ENHANCEMENT: Page Refresh Persistence
 * ---------------------------------------------
 * Currently, if a user refreshes the page AFTER completing file validation
 * and clicking "Confirm & Submit", the form state is lost (DataProfile is in-memory only).
 *
 * To fix this, we need:
 * 1. Backend: Add a status field to ingestion record (e.g., status: "validated" | "pending_ingest")
 * 2. Backend: Store the DataProfile/column metadata on the ingestion record
 * 3. Frontend: On page load, check URL for ?ingestion=<id>
 * 4. Frontend: If ingestion exists with status="validated", fetch and restore DataProfile
 * 5. Frontend: Re-populate form fields from the stored profile
 *
 * This would allow users to refresh or navigate away and return to their in-progress ingestion.
 * The IngestionContext (in file-validation/) already handles URL persistence for the validation
 * flow itself; this context would need similar integration for the post-validation form state.
 *
 * Related tickets: SCDM-2287, SCDM-2290
 * @see IngestionContext.tsx for how URL persistence works during validation
 */
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { DataProfile } from "datasets/pages/tables/components/file-validation/types";

interface IngestContextType {
  /** DataProfile from file validation - populated after user confirms validation */
  dataProfile: DataProfile | null;
  setDataProfile: Dispatch<SetStateAction<DataProfile | null>>;
  /** Form submission errors */
  errors: string[];
  setErrors: Dispatch<SetStateAction<string[]>>;
  /** Whether the user has confirmed their file selection (via validation modal or hydration) */
  isFileConfirmed: boolean;
  setIsFileConfirmed: Dispatch<SetStateAction<boolean>>;
  /** True while the debounced table name uniqueness check is in-flight. */
  isCheckingTableName: boolean;
  setIsCheckingTableName: Dispatch<SetStateAction<boolean>>;
  /** True when the debounced table name resolves to an existing table in the catalog. */
  tableExists: boolean;
  setTableExists: Dispatch<SetStateAction<boolean>>;
}

const IngestContext = createContext<IngestContextType | undefined>(undefined);

export const IngestProvider = ({ children }: { children: ReactNode }) => {
  const [dataProfile, setDataProfile] = useState<DataProfile | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isCheckingTableName, setIsCheckingTableName] = useState(false);
  const [isFileConfirmed, setIsFileConfirmed] = useState(false);
  const [tableExists, setTableExists] = useState(false);

  return (
    <IngestContext.Provider
      value={{
        dataProfile,
        setDataProfile,
        errors,
        setErrors,
        isCheckingTableName,
        setIsCheckingTableName,
        isFileConfirmed,
        setIsFileConfirmed,
        tableExists,
        setTableExists,
      }}
    >
      {children}
    </IngestContext.Provider>
  );
};

const useIngestContext = (): IngestContextType => {
  const context = useContext(IngestContext);

  if (!context) {
    throw new Error("useIngestContext must be used within an IngestProvider");
  }

  return context;
};

export default useIngestContext;
